import React, { useCallback, useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, Switch, Text, View } from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { TextInput as PaperTextInput } from 'react-native-paper';
import CountryPicker, {
  type Country,
  type CountryCode,
} from 'react-native-country-picker-modal';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../redux/helper';
import type { RootState } from '../../../redux/types';
import {
  addSingleSetting,
  selectProductCard,
  selectProductCardLayout,
} from '../../../redux/SettingsSlice';
import { updateLocationSetting } from '../../../redux/locationSlice';
import useLocation from '../../../hooks/useLocation';
import useBackofficeAxios from '../../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../../hooks/useBackofficeOwnerAuth';
import { updateLocationSettings } from '../../../api/locations';
import { updateOwnerBusinessInformation } from '../../../api/backoffice';
import { AppSnackbar } from '../../../services/AppSnackbar';
import { AppAlert } from '../../../services/AppAlert';
import { getApiMessage } from '../../../utils/apiError';
import { getLocationTimezoneUpdatePatch } from '../../../utils/timezone';
import { isEmployeeAdminRole } from '../../../utils/roles';
import { GTFonts, useTheme, type Theme } from '../../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../../utils/libs';

/**
 * Wizard preferences step (US3.5). Lets the new owner confirm/adjust the
 * defaults that shape day-to-day selling before landing on Home. All controls
 * are pre-filled with the current defaults; the step is fully skippable.
 *
 * - Product card layout: device-level (redux-persist).
 * - Currency + allow-price-edit: current location (backend), owner/admin only.
 *
 * Writes are optimistic and non-blocking; a failed network write reverts the
 * affected control and surfaces a snackbar without stopping the wizard.
 */

type SegmentOption<T extends string> = {
  value: T;
  label: string;
  icon: string;
};

const LAYOUT_OPTIONS: SegmentOption<'row' | 'grid'>[] = [
  { value: 'row', label: 'List', icon: 'view-list' },
  { value: 'grid', label: 'Grid', icon: 'view-grid-outline' },
];

const PreferencesStep: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const dispatch = useDispatch();

  const layout = useAppSelector(selectProductCardLayout);
  const productCard = useAppSelector(selectProductCard);

  const employeeData = useAppSelector(
    (state: RootState) => state.auth.inApp.account.employeeInfo,
  );
  const isAdmin = isEmployeeAdminRole(employeeData?.bp_employee_role);
  const { currentLocation, fetchLocations } = useLocation();
  const axiosPrivate = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();

  const canEditLocationSettings = Boolean(
    isAdmin && currentLocation?._id && owner?.accessToken,
  );

  // Currency ---------------------------------------------------------------
  const [currencyPickerVisible, setCurrencyPickerVisible] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] =
    useState<CountryCode>('KE');
  const [localCurrency, setLocalCurrency] = useState<string | null>(null);
  const [updatingCurrency, setUpdatingCurrency] = useState(false);

  const currentCurrency =
    localCurrency ??
    currentLocation?.bp_location_settings?.bp_location_currency ??
    'KES';

  // Allow editing selling price -------------------------------------------
  const initialAllowPriceEdit =
    currentLocation?.bp_location_settings
      ?.bp_location_allow_price_edit_during_checkout ?? true;
  const [allowPriceEdit, setAllowPriceEdit] = useState<boolean>(
    initialAllowPriceEdit,
  );
  const [updatingPriceEdit, setUpdatingPriceEdit] = useState(false);

  const handleLayoutChange = useCallback(
    (next: 'row' | 'grid') => {
      if (next === layout) return;
      dispatch(addSingleSetting({ productCard: { ...productCard, layout: next } }));
    },
    [dispatch, layout, productCard],
  );

  const handleCurrencyChange = useCallback(
    async (currency: string) => {
      if (!canEditLocationSettings) return;
      const next = String(currency || 'KES');
      const previous = currentCurrency;
      setLocalCurrency(next);
      setUpdatingCurrency(true);
      try {
        const payload = {
          bp_location_currency: next,
          ...getLocationTimezoneUpdatePatch(
            currentLocation?.bp_location_settings?.bp_location_timezone,
          ),
        };
        dispatch(updateLocationSetting({ bp_location_currency: next }));
        const res = await updateOwnerBusinessInformation(
          axiosPrivate as any,
          String(currentLocation!._id),
          payload,
          { accessToken: owner!.accessToken },
        );
        if (res?.status === 200) {
          await fetchLocations();
        } else {
          setLocalCurrency(previous);
          dispatch(updateLocationSetting({ bp_location_currency: previous }));
          AppSnackbar.show('Could not update currency', 'error');
        }
      } catch (e: any) {
        setLocalCurrency(previous);
        dispatch(updateLocationSetting({ bp_location_currency: previous }));
        AppSnackbar.show(getApiMessage(e, 'Could not update currency'), 'error');
      } finally {
        setUpdatingCurrency(false);
      }
    },
    [
      axiosPrivate,
      canEditLocationSettings,
      currentCurrency,
      currentLocation,
      dispatch,
      fetchLocations,
      owner,
    ],
  );

  const handleTogglePriceEdit = useCallback(
    async (next: boolean) => {
      if (!canEditLocationSettings) {
        AppAlert.alert('Not available', 'Missing location or permissions.');
        return;
      }
      setAllowPriceEdit(next);
      setUpdatingPriceEdit(true);
      try {
        const payload = {
          bp_location_allow_price_edit_during_checkout: next,
          ...getLocationTimezoneUpdatePatch(
            currentLocation?.bp_location_settings?.bp_location_timezone,
          ),
        };
        dispatch(
          updateLocationSetting({
            bp_location_allow_price_edit_during_checkout: next,
          }),
        );
        const res = await updateLocationSettings(
          axiosPrivate as any,
          String(currentLocation!._id),
          payload,
          { accessToken: owner!.accessToken },
        );
        if (res?.status !== 200) {
          setAllowPriceEdit(prev => !prev);
          dispatch(
            updateLocationSetting({
              bp_location_allow_price_edit_during_checkout: !next,
            }),
          );
          AppSnackbar.show('Could not update setting', 'error');
        }
      } catch (e: any) {
        setAllowPriceEdit(prev => !prev);
        dispatch(
          updateLocationSetting({
            bp_location_allow_price_edit_during_checkout: !next,
          }),
        );
        AppSnackbar.show(getApiMessage(e, 'Could not update setting'), 'error');
      } finally {
        setUpdatingPriceEdit(false);
      }
    },
    [axiosPrivate, canEditLocationSettings, currentLocation, dispatch, owner],
  );

  const renderSegment = <T extends string>(
    options: SegmentOption<T>[],
    selected: T,
    onSelect: (value: T) => void,
    testIDPrefix: string,
  ) => (
    <View style={styles.segmentRow}>
      {options.map(option => {
        const active = option.value === selected;
        return (
          <Pressable
            key={option.value}
            onPress={() => onSelect(option.value)}
            testID={`${testIDPrefix}-${option.value}`}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            style={({ pressed }: any) => [
              styles.segment,
              {
                borderColor: active ? theme.colors.success : theme.colors.border,
              },
              pressed && { opacity: 0.85 },
            ]}>
            <MaterialCommunityIcons
              name={option.icon as any}
              size={iconSize(22)}
              color={theme.colors.text}
            />
            <Text style={styles.segmentLabel}>{option.label}</Text>
            {active ? (
              <MaterialCommunityIcons
                name="check-circle"
                size={iconSize(18)}
                color={theme.colors.success}
                style={{ marginLeft: 'auto' }}
              />
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );

  return (
    <View style={styles.root} testID="preferences-step">
      <View style={styles.field}>
        <Text style={styles.fieldTitle}>Product card layout</Text>
        <Text style={styles.fieldSubtitle}>How your products appear</Text>
        {renderSegment(
          LAYOUT_OPTIONS,
          layout,
          handleLayoutChange,
          'preferences-layout',
        )}
      </View>

      {isAdmin ? (
        <>
          <View style={styles.field}>
            <Text style={styles.fieldTitle}>Currency</Text>
            <Text style={styles.fieldSubtitle}>
              Used for product prices and receipts.
            </Text>
            <Pressable
              testID="preferences-currency"
              onPress={() => setCurrencyPickerVisible(true)}
              disabled={updatingCurrency || !canEditLocationSettings}>
              <PaperTextInput
                mode="outlined"
                label="Currency"
                value={currentCurrency}
                editable={false}
                outlineColor={theme.colors.borderGray}
                activeOutlineColor={theme.colors.primary}
                onPress={() => setCurrencyPickerVisible(true)}
                style={styles.currencyInput}
                right={
                  <PaperTextInput.Icon
                    icon="chevron-down"
                    color={theme.colors.primary}
                    onPress={() => setCurrencyPickerVisible(true)}
                  />
                }
              />
            </Pressable>
            <CountryPicker
              containerButtonStyle={{ display: 'none' }}
              withFilter
              withFlag
              withCurrency
              withCurrencyButton
              withAlphaFilter
              countryCode={selectedCountryCode}
              visible={currencyPickerVisible}
              onClose={() => setCurrencyPickerVisible(false)}
              onSelect={(country: Country) => {
                const currency = (country as any)?.currency;
                const code = Array.isArray(currency) ? currency[0] : currency;
                if (code) handleCurrencyChange(code);
                if ((country as any)?.cca2) {
                  setSelectedCountryCode(
                    ((country as any).cca2 || 'KE') as CountryCode,
                  );
                }
                setCurrencyPickerVisible(false);
              }}
            />
          </View>

          <View style={[styles.field, styles.switchField]}>
            <View style={styles.switchText}>
              <Text style={styles.fieldTitle}>Allow editing selling price</Text>
              <Text style={styles.fieldSubtitle}>
              Allow cashiers to edit selling price up or down during checkout {'\n'}i.e. when a customer asks for a discount.
              </Text>
            </View>
            <Switch
              testID="preferences-allow-price-edit"
              trackColor={{ false: theme.colors.textSecondary, true: theme.colors.primary }}
              thumbColor={theme.colors.surface}
              ios_backgroundColor={theme.colors.text}
              onValueChange={handleTogglePriceEdit}
              value={Boolean(allowPriceEdit)}
              disabled={updatingPriceEdit || !canEditLocationSettings}
            />
          </View>
        </>
      ) : null}
    </View>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    root: {
      gap: v(20),
      marginTop: v(24),
    },
    field: {
    },
    fieldTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    fieldSubtitle: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      marginBottom: v(12),
      color: theme.colors.textSecondary,
    },
    segmentRow: {
      flexDirection: 'row',
      gap: s(8),
      marginTop: v(2),
    },
    segment: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: borderWidth(1.5),
      borderRadius: theme.borderRadius.md,
      paddingVertical: s(10),
      paddingHorizontal: s(10),
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web'
        ? ({ cursor: 'pointer' } as any)
        : {}),
    },
    segmentLabel: {
      marginLeft: s(6),
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      color: theme.colors.text,
    },
    currencyInput: {
      backgroundColor: theme.colors.surface,
      height: v(50),
    },
    switchField: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(12),
    },
    switchText: {
      flex: 1,
      gap: v(4),
    },
  });

export default PreferencesStep;
